import { BrowserRouter } from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Refine } from "@refinedev/core";
import { DevtoolsProvider } from "@refinedev/devtools";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import { RefineSnackbarProvider } from "@refinedev/mui";
import {
  DocumentTitleHandler,
  UnsavedChangesNotifier,
} from "@refinedev/react-router";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import * as Sentry from '@sentry/react';
import { browserTracingIntegration } from '@sentry/react';

import { ptBR } from "date-fns/locale";

import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";

import { queryClient, useI18nProvider, refineConfig } from "@core/configs";
import { ColorModeProvider } from "@core/providers/ColorModeProvider";
import { AppRoutes } from "@core/routes/routes";

function App() {
  const i18nProvider = useI18nProvider();

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    tracesSampleRate:
      parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "0.0") ||
      0.0,
    release: import.meta.env.VITE_SENTRY_RELEASE || "unknown-release",
    integrations: [browserTracingIntegration],
  });

  const customTitleHandler = ({ resource, action }: any) => {
    let title = "Partiu";
    if (resource && action) {
      title = `${resource.label} | Partiu`;
    }
    return title;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <RefineKbarProvider>
            <ColorModeProvider>
              <CssBaseline />
              <GlobalStyles
                styles={{ html: { WebkitFontSmoothing: "auto" } }}
              />
              <RefineSnackbarProvider>
                <DevtoolsProvider>
                  <Refine {...refineConfig} i18nProvider={i18nProvider}>
                    <AppRoutes />
                    <RefineKbar />
                    <ReactQueryDevtools initialIsOpen={false} />
                    <UnsavedChangesNotifier />
                    <DocumentTitleHandler handler={customTitleHandler} />
                  </Refine>
                </DevtoolsProvider>
              </RefineSnackbarProvider>
            </ColorModeProvider>
          </RefineKbarProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </LocalizationProvider>
  );
}

export default App;
